import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, FileText, Receipt, Send, CheckCircle2, XCircle, Download, History } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  leave_submitted: { label: "Congé soumis", icon: Send, cls: "bg-blue-50 text-blue-700 border-blue-100" },
  leave_approuve: { label: "Congé approuvé", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  leave_rejete: { label: "Congé rejeté", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-100" },
  leave_pdf_downloaded: { label: "Lettre de congé téléchargée", icon: Download, cls: "bg-slate-50 text-slate-700 border-slate-200" },
  certificate_submitted: { label: "Attestation demandée", icon: Send, cls: "bg-blue-50 text-blue-700 border-blue-100" },
  certificate_approuve: { label: "Attestation validée", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  certificate_rejete: { label: "Attestation rejetée", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-100" },
  certificate_pdf_downloaded: { label: "Attestation téléchargée", icon: Download, cls: "bg-slate-50 text-slate-700 border-slate-200" },
  payslip_uploaded: { label: "Bulletin déposé", icon: Receipt, cls: "bg-amber-50 text-amber-700 border-amber-100" },
  payslip_downloaded: { label: "Bulletin téléchargé", icon: Download, cls: "bg-slate-50 text-slate-700 border-slate-200" },
};

const TARGET_ICON = { leave: CalendarDays, certificate: FileText, payslip: Receipt };

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function RHAuditLogs() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { target_type: filter };
      const { data } = await api.get("/audit-logs", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div>
      <PageHeader
        title="Journal des documents"
        subtitle="Historique complet des actions : demandes, validations et téléchargements des documents RH."
        action={
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="audit-filter-select" className="w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                <SelectItem value="leave">Congés</SelectItem>
                <SelectItem value="certificate">Attestations</SelectItem>
                <SelectItem value="payslip">Bulletins de paie</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      <Card className="border-slate-200">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <History className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Aucune activité enregistrée</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">Date</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Concerné</TableHead>
                <TableHead>Par</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((log) => {
                const meta = ACTION_META[log.action] || { label: log.action, icon: History, cls: "bg-slate-50 text-slate-700 border-slate-200" };
                const AIcon = meta.icon;
                const TIcon = TARGET_ICON[log.target_type] || FileText;
                return (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(log.created_at)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.cls}`}>
                        <AIcon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TIcon className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{log.target_label || log.target_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-900">{log.target_user_name || "—"}</div>
                      <div className="text-xs text-slate-500">{log.target_user_email || ""}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700">{log.actor_name}</div>
                      <div className="text-xs text-slate-500">{log.actor_role === "rh" ? "RH" : "Employé"}</div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
